import { BaseModel, EndOfDate, FormattedDateString, StartOfDate } from '@red/data-access';
import { Expose, Transform, Type } from 'class-transformer';
import { IStatusDescription } from '../interfaces/status-description.interface';
import { IPaymentReminderSkip } from '../interfaces/payment-reminder-skip.interface';
import { PAYMENT_REMINDER_STATUS, PaymentReminderStatusItem, PRE_LOD_EMAIL_TYPE_OPTIONS, RESALE_STATUSES, RESALE_TYPE_OPTIONS, STAGE_OPTIONS } from '../data';
import { EResaleStatus, EResaleType } from '../enums/resale.enum';
import { EReminderStage, EPaymentReminderEmailStatus, EPaymentReminderStatus, EPreLodEmailType } from '../enums/payment-reminder.enum';
import { EMessageOutboxStatus } from '../..';

export class PaymentReminderInfoModel extends BaseModel {
  @Expose()
  @Type(() => Number)
  id!: number;

  @Expose()
  createdAt!: string;

  @Expose()
  updatedAt!: string;

  @Expose()
  createdBy!: number;

  @Expose()
  updatedBy!: number;

  @Expose()
  paymentReminderId!: number;

  @Expose()
  emailStatus!: EPaymentReminderEmailStatus;

  @Expose()
  sentAt!: string;

  @Expose()
  stage!: string;

  @Expose()
  errorCode!: string | null;

  @Expose()
  errorMessage!: string | null;
}

export class PaymentReminderModel extends BaseModel {
  @Expose()
  @Type(() => Number)
  id!: number;

  @Expose()
  resaleCode!: string;

  @Expose()
  invoiceCode!: string;

  @Expose()
  invoiceDate!: string;

  @Expose()
  outstandingAmount!: number;

  @Expose()
  clientName!: string;

  @Expose()
  propertyAddress!: string;

  @Expose()
  agentId!: number;

  @Expose()
  agentName!: string;

  @Expose()
  agentCea!: string;

  @Expose()
  resaleStatus!: EResaleStatus;

  @Expose()
  resaleType!: EResaleType;

  @Expose()
  commencementDate!: string | null;

  @Expose()
  completionDate!: string | null;

  isHover?: boolean;

  @Expose()
  firstReminderDueDate!: string | null;

  @Expose()
  firstReminderIssueDate!: string | null;

  @Expose()
  finalReminderDueDate!: string | null;

  @Expose()
  lodDueDate!: string | null;

  @Expose()
  debtRecoveryOption!: string;

  @Expose()
  effectiveDate!: string | null;

  @Expose()
  agreementDate!: string | null;

  @Expose()
  debtRecoveryIssueDate?: string;

  @Expose()
  arbitrationIssueDate!: string | null;

  @Expose()
  mediationIssueDate!: string | null;

  @Expose()
  actualWriteOffDate!: string | null;

  @Expose()
  status!: EPaymentReminderStatus;

  @Expose()
  remarks!: string;

  @Expose()
  firstReminderLetterKey!: string;

  @Expose()
  finalReminderIssueDate!: string | null;

  @Expose()
  finalReminderLetterKey!: string;

  @Expose()
  lodIssueDate!: string | null;

  @Expose()
  lodLetterKey!: string;

  @Expose()
  firstReminderRevertReason!: string | null;

  @Expose()
  finalReminderRevertReason!: string | null;

  @Expose()
  lodRevertReason!: string | null;

  @Expose()
  emailLockedAt!: string

  @Expose()
  @Type(() => PaymentReminderInfoModel)
  paymentReminderInfos!: PaymentReminderInfoModel[];

  @Expose()
  skip!: IPaymentReminderSkip | null;

  @Expose()
  writeOffReason!: string | null;

  @Expose()
  additionalDetails!: string | null;

  @Expose()
  writeOffDate!: string | null;

  @Expose()
  metadata!: any;

  @Expose()
  get firstReminderEmailSent(): PaymentReminderInfoModel | undefined {
    return this.paymentReminderInfos?.find(i => i.stage === EReminderStage.firstReminder);
  }

  @Expose()
  get finalReminderEmailSent(): PaymentReminderInfoModel | undefined {
    return this.paymentReminderInfos?.find(i => i.stage === EReminderStage.finalReminder);
  }

  @Expose()
  get lodEmailSent(): PaymentReminderInfoModel | undefined {
    return this.paymentReminderInfos?.find(i => i.stage === EReminderStage.lod);
  }

  @Expose()
  get resaleStatusDisplay(): IStatusDescription {
    return RESALE_STATUSES[this.resaleStatus];
  }

  @Expose()
  get resaleTypeDescription() {
    return this.resaleType ? RESALE_TYPE_OPTIONS[this.resaleType] : null;
  }

  @Expose()
  get statusDisplay(): PaymentReminderStatusItem {
    return PAYMENT_REMINDER_STATUS[this.status];
  }

  @Expose()
  get canSendPreLod(): boolean {
    return Object.values(this.metadata.preLod ?? []).length !== Object.values(PRE_LOD_EMAIL_TYPE_OPTIONS).length;
  }
}


export class PaymentReminderDeliveryFilters extends BaseModel {
  @Expose()
  key!: string;

  @Expose()
  searchType!: string;

  @Expose()
  page!: number;

  @Expose()
  limit!: number;

  @Expose()
  orderBy!: string;

  @Expose()
  orderType!: string;

  @Expose()
  reference!: string;

  @Expose()
  @StartOfDate()
  @FormattedDateString()
  invoiceDateStart!: string;

  @Expose()
  @EndOfDate()
  @FormattedDateString()
  invoiceDateEnd!: string;

  @Expose()
  @StartOfDate()
  @FormattedDateString()
  sentOnStart!: string;

  @Expose()
  @EndOfDate()
  @FormattedDateString()
  sentOnEnd!: string;

  @Expose()
  propertyAddress!: string;

  @Expose()
  emailStatus!: string;

  @Expose()
  stage!: string;
}


export class ReminderDeliveryModel extends BaseModel {
  @Expose()
  id!: number;

  @Expose()
  resaleCode!: string;

  @Expose()
  invoiceCode!: string;

  @Expose()
  invoiceDate!: string;

  @Expose()
  emailStatus!: EPaymentReminderEmailStatus;

  @Expose()
  stage!: string;

  @Expose()
  agentName!: string;

  @Expose()
  fromEmail!: string;

  @Expose()
  toEmail!: string;

  @Expose()
  emailType!: EReminderStage;

  @Expose()
  subject!: string;

  @Expose()
  propertyAddress!: string;

  @Expose()
  body!: string;

  @Expose()
  createdAt!: string;

  @Expose()
  updatedAt!: string;

  @Expose()
  sentAt!: string;

  @Expose()
  cc!: string[];

  @Expose()
  attachments!: {
    filename: string;
    type: string;
  }[];

  @Expose()
  errorMessage!: string;

  @Expose()
  @Transform(({ obj }: { obj: any }) => {
    switch (obj.emailStatus) {
      case EPaymentReminderEmailStatus.sent:
        return {
          className: 'status-box-success',
          value: obj.emailStatus,
          displayName: 'Processed',
        };
      case EPaymentReminderEmailStatus.failed:
        return {
          className: 'status-box-default',
          value: obj.emailStatus,
          displayName: 'Failed',
        };
      default:
        return {
          className: 'status-box-default',
          value: obj.emailStatus,
          displayName: obj.emailStatus,
        };
    }
  })
  statusObj!: { className: string; value: string; displayName: string };


  @Expose()
  get emailTypeDisplay() {
    return STAGE_OPTIONS[this.stage as EReminderStage]?.viewValue ?? PRE_LOD_EMAIL_TYPE_OPTIONS[this.stage as EPreLodEmailType]?.viewValue;
  }

  @Expose()
  get canResend(): boolean {
    return this.emailStatus === EPaymentReminderEmailStatus.failed;
  }
};
